import React, { useEffect, useMemo, useRef, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import useMobile from "../../components/Hooks/Mobile";
import generateURL from "../../helper/vehicle_url";
import categories from "../../helper/categories";
import { vehicleCompareEvent } from "../../utils/Analytics";
import { googleAdIds } from "../../helper/constants";

import Footer from "../../components/Footer";
import Header from "../../components/Header";
import PageHeader from "../../components/PageHeader";
import EVsSelector from "../../components/EVsSelection/EVsSelector";
import ComparisonTable from "../../components/ComparisonTable";
import GoogleAd from "../../components/GoogleAd";

import styles from "./styles.module.scss";
import SearchPageBackground from '../../public/assets/images/headers/vehicle-comparison-header.jpg';
import { useIsBoiWebsite } from "../../components/Context/WebsiteType";

// to process URL
// for make use make name and our id
// for model it uses model name and third_party id
// for variant just use third_party id to fetch a variant...

export default function VehiclesComparisonPage() {
	const [vehicles, setVehicles] = useState<any[]>([]);

	// undefined - initial | false - ommited or processed | true - processing
	const [processURL, setProcessURL] = useState<undefined | boolean>();
	const [query, setQuery] = useState<any>();
	const router = useRouter();

	const smallerScreen = useMobile(730);
	const mobile = useMobile(627);

	const comparisonRef = useRef<HTMLDivElement>(null);

	const comparisonReady = vehicles.length > 1;

	const isBoiWebsite = useIsBoiWebsite();

	useEffect(() => {
		if (mobile && vehicles.length > 2) {
			setVehicles(vehicles.slice(0, 2));
		}
	}, [mobile, vehicles]);

	useEffect(() => {
		if (router.isReady && processURL === undefined) {
			// Ensuring it'll run only once
			if (Object.keys(router.query).length !== 0) {
				// if there are keys - start processing URL
				setProcessURL(true);
				setQuery(router.query);
			} else {
				setProcessURL(false);
			}
		}
	}, [router, processURL]);

	useEffect(() => {
		// updating url with selected vehicles
		if (processURL === false) {
			// when stopped processing URL
			let url = `/vehicles-comparison`;

			if (vehicles.length > 0) {
				const query = vehicles.map(({ value }, index) => generateURL(value, index + 1)).join("&");
				url += `?${query}`;
			}

			if (router.asPath !== url) {
				// prevent looping
				router.replace(url, undefined, { shallow: true });
			}
		}
	}, [router, vehicles, processURL]);

	useEffect(() => {
		if (mobile && vehicles.length === 2) {
			comparisonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [mobile, vehicles]);

	// tracking compared vehicles
	useEffect(() => {
		if (vehicles.length > 1) {
			vehicleCompareEvent(isBoiWebsite, vehicles.map(({ value }) => value.id));
		}
	}, [vehicles, isBoiWebsite]);

	return (
		<>
			<Head>
				<title>Vehicles - {categories.compareEV} | Nevo</title>
			</Head>

			<Header transparent />

			<PageHeader backgroundImage={SearchPageBackground.src} className={isBoiWebsite ? styles.pageHeaderBoi : styles.pageHeader} faded>
				<div className={isBoiWebsite ? styles.pageHeaderContentBoi : styles.pageHeaderContent}>
					<h1>
						{smallerScreen ? (
							<span>
								{"It's time."}
								<br />
								{"Select and compare."}
							</span>
						) : (
							"It's time. Select and compare."
						)}
					</h1>
				</div>
			</PageHeader>

			<div className={`${isBoiWebsite ? styles.vehicleComparisonPageBoi : styles.vehicleComparisonPage} page-holder`}>
				{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} className={styles.adBanner} evComparisonPage />}

				<div className={isBoiWebsite ? styles.evsHolderBoi : styles.evsHolder}>
					<EVsSelector setVehicles={setVehicles} mobile={mobile} processURL={processURL} setProcessURL={setProcessURL} query={query} />
				</div>

				<div className={isBoiWebsite ? styles.resultsHolderBoi : styles.resultsHolder} ref={comparisonRef}>
					<h1>{comparisonReady ? "Comparison results" : `Select ${!mobile ? "at least " : ""}2 cars to compare`}</h1>

					{comparisonReady && (
						<div className={isBoiWebsite ? styles.resultsBoi : styles.results}>
							<ComparisonTable vehicles={vehicles.map((vehicle) => (vehicle = vehicle.value))} mobile={mobile} />
						</div>
					)}
				</div>
			</div>

			<Footer />
		</>
	);
}
